import React, { useEffect, useState } from "react";
import { Nav, INavProps } from "./Nav";
import { ILinkFunc } from "../../lib/util";
import { IMediaItem, ISocialLink } from "../../lib";
import { TextWidgetSearch } from "../Search";

export enum EHeaderLayout {
  top,
  side,
  top_spread
}

export interface IHeaderProps {
  className?: string;
  logo: IMediaItem;
  skipLinkTarget?: string;
  navProps: INavProps;
  absolute?: boolean;
  gradient?: boolean;
  linkFunc?: ILinkFunc;
  layout?: EHeaderLayout;
  onSubmit?: (filters: {}) => void;
  searchPath?: string;
  searchText?: string;
  addFlagOnScroll?: boolean;
  upperLinks?: {
    href: string;
    text: string;
    target?: string;
  }[];
  socialMediaLinks?: ISocialLink[];
}

export const Header: React.FC<IHeaderProps> = ({
  className = "",
  skipLinkTarget = "#main",
  logo,
  navProps,
  linkFunc,
  absolute = false,
  gradient = false,
  layout = EHeaderLayout.top,
  onSubmit,
  searchPath,
  searchText,
  addFlagOnScroll = false,
  upperLinks,
  socialMediaLinks,
}) => {
  const layoutClasses = {
    header: "",
    navLogoContainer: "",
    logo: "",
    nav: "",
    search: "",
  };

  switch (layout) {
    case EHeaderLayout.top:
      layoutClasses.header = `${absolute ? "relative md:absolute" : "relative"
        } w-full ${gradient &&
        "md:bg-transparent md:bg-gradient-to-b from-white to-transparent md:pb-16 px-2 md:px-0"
        }`;
      layoutClasses.navLogoContainer =
        "flex md:px-5 max-w-screen-2xl mx-auto md:nowrap py-3 justify-between items-center";
      layoutClasses.logo = "max-w-[69%] py-3";
      break;
    case EHeaderLayout.top_spread:
      layoutClasses.header = `${absolute ? "relative md:absolute" : "relative"
        } w-full ${gradient &&
        "md:bg-transparent md:bg-gradient-to-b from-black to-transparent md:pb-16 px-2 md:px-0"
        }`;
      layoutClasses.navLogoContainer =
        "flex md:px-5 max-w-screen-2xl mx-auto md:nowrap py-3 justify-between items-center";
      layoutClasses.logo = "max-w-[69%] py-3";
      break;
    case EHeaderLayout.side:
      layoutClasses.header =
        "fixed top-0 right-0 md:right-auto md:bottom-0 left-0  md:w-[100px] inverted bg-body-bg/50";
      layoutClasses.navLogoContainer =
        "w-full md:w-auto h-full inverted flex md:justify-between items-center md:items-stretch md:flex-col";
      layoutClasses.logo = "order-2 max-w-[110px] max-h-[70px] md:max-h-auto";
      layoutClasses.nav = "order-1";
      layoutClasses.search =
        "absolute right-0 md:static order-3 text-3xl md:text-5xl min-h-[70px] md:min-h-[100px] min-w-[70px] md:min-w-[100px] flex justify-center items-center border-l md:border-l-0 md:border-t z-20";
      break;
  }

  const linkf = linkFunc || ((props) => <a {...props} />);

  const [isScrolledDown, setIsScrolledDown] = useState(false);
  if (addFlagOnScroll) {
    useEffect(() => {
      let timeoutId: any;

      function setHeaderClass() {
        const hasWindowObj = typeof window !== "undefined"
        const screenHeight = hasWindowObj ? window.innerHeight : 0
        const scrollTop = hasWindowObj ? window.scrollY : 0
        if (scrollTop > screenHeight / 2) {
          setIsScrolledDown(true)
        }
        else {
          setIsScrolledDown(false)
        }
      }
      function onScroll() {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(setHeaderClass, 500)
      }

      document.addEventListener('scroll', onScroll)

      return () => {
        document.removeEventListener('scroll', onScroll)
      }
    }, [isScrolledDown]);
  }

  return (
    <header className={`z-30 ${layoutClasses.header} ${className} ${isScrolledDown ? "is-scrolled" : ""}`}>
      {layout != EHeaderLayout.side && upperLinks?.[0] && (
        <ul className="list-none max-w-screen-2xl mx-auto text-sm gap-x-8 hidden w-full md:flex md:flex-nowrap md:px-5 md:pt-3">
          {upperLinks.map((item, index) => (
            <li className="relative whitespace-nowrap">
              {linkf({
                href: item.href,
                target: item.target,
                className: "text-body-text hover:font-extrabold hover:underline",
                key: index,
                children: item.text
              })}
            </li>
          ))}
        </ul>
      )}
      <a
        className="sr-only focus:not-sr-only focus:absolute top-0 left-0"
        href={skipLinkTarget}
      >
        Skip Navigation Links
      </a>
      <div className={layoutClasses.navLogoContainer}>
        {logo &&
          linkf({
            href: "/",
            className: `${layoutClasses.logo}`,
            title: logo.caption,
            children: (
              <>
                <span className="sr-only">Homepage</span>
                {!logo.sources && (
                  <img
                    src={logo.url}
                    width={logo.dimensions?.[0]}
                    height={logo.dimensions?.[1]}
                    alt={logo.caption}
                    className="max-h-28 sm:max-h-40 md:w-[18vw] w-[26vw]"
                  />
                )}
                {logo.sources && (
                  <picture>
                    {logo.sources.map((source, key) => (
                      <source
                        srcSet={source.srcSet}
                        media={source.media}
                        key={key}
                      />
                    ))}
                    <img
                      src={logo.url}
                      width={logo.dimensions?.[0]}
                      height={logo.dimensions?.[1]}
                      alt={logo.caption}
                      className="max-h-28 sm:max-h-40 md:w-[18vw] w-[26vw]"
                    />
                  </picture>
                )}
              </>
            ),
          })}
        <Nav
          linkFunc={linkf}
          {...navProps}
          layout={layout}
          className={layoutClasses.nav}
          socialMediaLinks={socialMediaLinks}
        />
        {searchPath && (
          <div className={layoutClasses.search}>
            <TextWidgetSearch
              placeholder={searchText || "Keywords"}
              onSubmit={navProps?.onSubmit || (() => { })}
              expand="right"
              openClassName="w-full"
            />
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
